// Commissions → Payouts: biweekly payout batches. Create a batch for a period,
// refresh it, leave items out (they carry forward), approve it, and — only
// once payroll has actually paid — record the payment. Paid batches are final.
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import {
  BATCH_STATUS_LABEL,
  BONUS_KIND_LABEL,
  type BatchDetailResponse,
  type BatchItem,
  type PayoutBatch,
  type PayoutCalendar,
  errReason,
  fmtDay,
  money,
  nameOr,
  periodKeyLabel,
  periodLabel,
  roleLabel,
  todayIso,
} from "@/lib/compPlans";
import { Button } from "@/components/ui/Button";
import type { GoToTab } from "./CommissionsPlansPage";
import { Drawer, Empty, Field, INPUT, Loading, Notice, Pill, Section, T_HEAD, T_NUM, T_ROW, T_ROW_CLICK, T_TD, T_TH, T_WRAP } from "./parts";

function batchTotal(b: PayoutBatch): number {
  return Number(b.commission_total) + Number(b.bonus_total) + Number(b.adjustment_total);
}

function batchTone(s: string): "active" | "warn" | "info" {
  return s === "paid" ? "active" : s === "approved" ? "warn" : "info";
}

export function PayoutsTab({ go }: { go: GoToTab }) {
  const qc = useQueryClient();
  const [params, setParams] = useSearchParams();
  const openBatch = params.get("batch");
  const [msg, setMsg] = useState<{ tone: "ok" | "risk"; text: string } | null>(null);
  const [justCreated, setJustCreated] = useState<string | null>(null);

  const q = useQuery({
    queryKey: ["commissions", "batches"],
    queryFn: () => api<{ calendar: PayoutCalendar; batches: PayoutBatch[] }>("/commissions/batches"),
  });

  const setBatch = (id: string | null) => {
    setMsg(null); // messages belong to the view they were made in
    if (!id) setJustCreated(null);
    const p = new URLSearchParams(params);
    if (id) p.set("batch", id);
    else p.delete("batch");
    setParams(p);
  };

  const create = useMutation({
    mutationFn: (periodStart: string) =>
      api<{ id: string }>("/commissions/batches", { method: "POST", body: JSON.stringify({ period_start: periodStart }) }),
    onSuccess: (r) => {
      void qc.invalidateQueries({ queryKey: ["commissions"] });
      setJustCreated(r.id);
      setBatch(r.id);
    },
    onError: (e) => setMsg({ tone: "risk", text: errReason(e) }),
  });

  if (q.isError) return <Notice tone="risk">{errReason(q.error)}</Notice>;
  if (!q.data) return <Loading />;
  const { calendar, batches } = q.data;
  const has = (start: string) => batches.some((b) => b.period_start === start);
  const periods = [
    calendar.current ? { label: "This period", ...calendar.current } : null,
    calendar.previous ? { label: "Last period", ...calendar.previous } : null,
  ].filter((p): p is { label: string; start: string; end: string } => p != null);
  const open = openBatch ? batches.find((b) => b.id === openBatch) : undefined;

  return (
    <div>
      {msg && (
        <Notice tone={msg.tone} className="mb-5">
          {msg.text}
        </Notice>
      )}

      {!calendar.anchor ? (
        <Notice tone="warn" className="mb-8">
          Set the payout calendar in Settings — batches follow fixed two-week periods from the date Finance chooses.{" "}
          <button type="button" className="font-semibold underline" onClick={() => go("settings")}>
            Go to Settings
          </button>
        </Notice>
      ) : (
        <Section
          title="Pay a period"
          intro="A batch takes everything eligible up to the end of its period — anything left out or not yet eligible carries forward to the next one."
        >
          <div className="grid gap-4 md:grid-cols-2">
            {periods.map((p) => (
              <div key={p.start} className="flex flex-wrap items-center justify-between gap-3 rounded-md border border-rule bg-white p-5">
                <div>
                  <div className="text-[14px] text-muted">{p.label}</div>
                  <div className="text-[18px] font-medium text-ink">{periodLabel(p.start, p.end)}</div>
                </div>
                {has(p.start) ? (
                  <Button variant="outline" onClick={() => setBatch(batches.find((b) => b.period_start === p.start)!.id)}>
                    Open batch
                  </Button>
                ) : (
                  <Button onClick={() => create.mutate(p.start)} disabled={create.isPending}>
                    Create batch
                  </Button>
                )}
              </div>
            ))}
          </div>
        </Section>
      )}

      <Section title="Batches">
        {batches.length === 0 ? (
          <Empty>No payout batches yet.</Empty>
        ) : (
          <div className={T_WRAP}>
            <table className="w-full min-w-[1100px]">
              <thead className={T_HEAD}>
                <tr>
                  <th className={T_TH}>Period</th>
                  <th className={T_TH}>Status</th>
                  <th className={`${T_TH} text-right`}>Commission</th>
                  <th className={`${T_TH} text-right`}>Bonuses</th>
                  <th className={`${T_TH} text-right`}>Adjustments</th>
                  <th className={`${T_TH} text-right`}>Total</th>
                  <th className={T_TH}>Approved</th>
                  <th className={T_TH}>Paid</th>
                </tr>
              </thead>
              <tbody>
                {batches.map((b) => (
                  <tr key={b.id} className={T_ROW_CLICK} onClick={() => setBatch(b.id)}>
                    <td className={`${T_TD} font-medium`}>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          setBatch(b.id);
                        }}
                        className="text-left underline-offset-2 hover:underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#63768b]"
                        aria-label={`Open payout batch ${periodLabel(b.period_start, b.period_end)}`}
                      >
                        {periodLabel(b.period_start, b.period_end)}
                      </button>
                    </td>
                    <td className={T_TD}>
                      <Pill tone={batchTone(b.status)}>{BATCH_STATUS_LABEL[b.status] ?? b.status}</Pill>
                    </td>
                    <td className={T_NUM}>{money(b.commission_total)}</td>
                    <td className={T_NUM}>{money(b.bonus_total)}</td>
                    <td className={T_NUM}>{money(b.adjustment_total)}</td>
                    <td className={`${T_NUM} font-semibold`}>{money(batchTotal(b))}</td>
                    <td className={T_TD}>{b.approved_at ? `${fmtDay(b.approved_at)}${b.approved_by_name ? ` · ${b.approved_by_name}` : ""}` : "—"}</td>
                    <td className={T_TD}>
                      {b.paid_on ? `${fmtDay(b.paid_on)}${b.payment_reference ? ` · ${b.payment_reference}` : ""}` : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Section>

      {openBatch && (
        <Drawer
          title={open ? `Payout ${periodLabel(open.period_start, open.period_end)}` : "Payout batch"}
          onClose={() => setBatch(null)}
        >
          <BatchDetail
            key={openBatch}
            batchId={openBatch}
            go={go}
            initialMsg={justCreated === openBatch ? "Batch created with everything eligible so far." : null}
          />
        </Drawer>
      )}
    </div>
  );
}

function itemLabel(i: { kind: string; ref: string | null; detail: string | null }): { what: string; detail: string } {
  if (i.kind === "commission") return { what: `Commission · order #${i.ref ?? "—"}`, detail: roleLabel(i.detail) };
  if (i.kind === "bonus") {
    return { what: `${BONUS_KIND_LABEL[i.detail ?? ""] ?? ""} bonus`.trim().replace(/^bonus$/, "Bonus"), detail: periodKeyLabel(i.ref) };
  }
  return { what: "Adjustment", detail: i.detail ?? "" };
}

type Msg = { tone: "ok" | "risk"; text: string } | null;

function BatchDetail({ batchId, go, initialMsg }: { batchId: string; go: GoToTab; initialMsg: string | null }) {
  const qc = useQueryClient();
  const key = ["commissions", "batch", batchId];
  const q = useQuery({
    queryKey: key,
    queryFn: () => api<BatchDetailResponse>(`/commissions/batches/${batchId}`),
  });
  const [msg, setMsg] = useState<Msg>(initialMsg ? { tone: "ok", text: initialMsg } : null);
  const [paidOn, setPaidOn] = useState(todayIso());
  const [reference, setReference] = useState("");
  const [confirmApprove, setConfirmApprove] = useState(false);
  const [confirmPaid, setConfirmPaid] = useState(false);
  const [confirmRemove, setConfirmRemove] = useState<string | null>(null);

  const act = useMutation({
    mutationFn: (a: { path: string; body?: unknown; ok: string }) =>
      api<BatchDetailResponse>(`/commissions/batches/${batchId}/${a.path}`, {
        method: "POST",
        body: JSON.stringify(a.body ?? {}),
      }).then((r) => ({ r, ok: a.ok })),
    onSuccess: ({ r, ok }) => {
      qc.setQueryData(key, r);
      setMsg({ tone: "ok", text: ok });
      setConfirmApprove(false);
      setConfirmPaid(false);
      setConfirmRemove(null);
      void qc.invalidateQueries({ queryKey: ["commissions"] });
    },
    onError: (e) => {
      setMsg({ tone: "risk", text: errReason(e) });
      setConfirmPaid(false);
    },
  });

  if (q.isError) return <Notice tone="risk">{errReason(q.error)}</Notice>;
  if (!q.data) return <Loading />;
  const { batch, items } = q.data;
  const leftOut = q.data.leftOut ?? [];
  const waiting = q.data.waiting ?? [];
  const isDraft = batch.status === "draft";
  const total = money(batchTotal(batch));
  const today = todayIso();
  const paidOnOk = /^\d{4}-\d{2}-\d{2}$/.test(paidOn) && paidOn <= today;

  const byRep = new Map<string, { name: string; items: BatchItem[]; total: number }>();
  for (const it of items) {
    const g = byRep.get(it.rep_id) ?? { name: nameOr(it.rep_name), items: [], total: 0 };
    g.items.push(it);
    g.total += Number(it.amount);
    byRep.set(it.rep_id, g);
  }

  return (
    <div>
      {msg && (
        <Notice tone={msg.tone} className="mb-5">
          {msg.text}
          {msg.tone === "risk" && /Settings/.test(msg.text) && (
            <>
              {" "}
              <button type="button" className="font-semibold underline" onClick={() => go("settings")}>
                Open Settings
              </button>
            </>
          )}
        </Notice>
      )}

      <div className="mb-6 flex flex-wrap items-center justify-between gap-4 rounded-md border border-rule bg-white p-5">
        <div className="flex flex-wrap items-center gap-4">
          <Pill tone={batchTone(batch.status)}>{BATCH_STATUS_LABEL[batch.status] ?? batch.status}</Pill>
          <span className="text-[24px] font-semibold tabular-nums text-ink">{total}</span>
          <span className="text-[15px] text-muted">
            {items.length} {items.length === 1 ? "item" : "items"} · {byRep.size} {byRep.size === 1 ? "rep" : "reps"}
          </span>
        </div>
        {isDraft && (
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" disabled={act.isPending} onClick={() => act.mutate({ path: "refresh", ok: "Refreshed — newly eligible items were added. Items you left out stay out." })}>
              Refresh
            </Button>
            {confirmApprove ? (
              <>
                <Button disabled={act.isPending} onClick={() => act.mutate({ path: "approve", ok: "Batch approved. Record the payment once payroll has paid it." })}>
                  Yes, approve {total}
                </Button>
                <Button variant="ghost" onClick={() => setConfirmApprove(false)}>
                  Cancel
                </Button>
              </>
            ) : (
              <Button disabled={items.length === 0} onClick={() => setConfirmApprove(true)}>
                Approve batch
              </Button>
            )}
          </div>
        )}
      </div>

      {isDraft && (
        <Notice tone="info" className="mb-6">
          Approving is not paying. Approve when the amounts are right; record the payment once payroll has paid.
        </Notice>
      )}

      {batch.status === "approved" && (
        <Section
          title="Record payment"
          intro="Approving is not paying. Record the payment only once payroll has actually paid these amounts — that is what marks each commission paid."
        >
          <div className="grid gap-4 rounded-md border border-rule bg-white p-5 md:grid-cols-[220px_minmax(0,1fr)_auto] md:items-end">
            <Field label="Paid on" hint={paidOn > today ? "Can't be in the future — record it once payroll has paid." : undefined}>
              <input
                type="date"
                className={INPUT}
                max={today}
                value={paidOn}
                onChange={(e) => {
                  setPaidOn(e.target.value);
                  setConfirmPaid(false);
                }}
              />
            </Field>
            <Field label="Payroll reference (optional)">
              <input className={INPUT} value={reference} onChange={(e) => setReference(e.target.value)} placeholder="e.g. QB Payroll run 10/30" />
            </Field>
            <div className="flex flex-wrap items-center gap-3">
              <span className="text-[15px] text-muted">
                Batch total <span className="text-[18px] font-semibold tabular-nums text-ink">{total}</span>
              </span>
              {confirmPaid ? (
                <>
                  <Button
                    disabled={!paidOnOk || act.isPending}
                    onClick={() =>
                      act.mutate({
                        path: "paid",
                        body: { paid_on: paidOn, reference: reference.trim() || null },
                        ok: "Payment recorded. This batch is now paid and can't change.",
                      })
                    }
                  >
                    Yes, record {total} paid on {fmtDay(paidOn)}
                  </Button>
                  <Button variant="ghost" onClick={() => setConfirmPaid(false)}>
                    Cancel
                  </Button>
                </>
              ) : (
                <Button disabled={!paidOnOk} onClick={() => setConfirmPaid(true)}>
                  Record payment
                </Button>
              )}
            </div>
          </div>
        </Section>
      )}

      {batch.status === "paid" && (
        <Notice tone="ok" className="mb-6">
          Paid {fmtDay(batch.paid_on)}
          {batch.payment_reference ? ` · ${batch.payment_reference}` : ""}
          {batch.paid_recorded_by_name ? ` · recorded by ${batch.paid_recorded_by_name}` : ""}. Paid batches can't change — correct anything
          with an adjustment.
        </Notice>
      )}

      {items.length === 0 ? (
        <Empty>Nothing in this batch. Refresh to pull in anything that has become eligible.</Empty>
      ) : (
        [...byRep.entries()].map(([repId, g]) => (
          <Section
            key={repId}
            title={
              <span className="flex flex-wrap items-baseline gap-3">
                {g.name}
                <span className="text-[17px] font-medium tabular-nums text-muted">{money(g.total)}</span>
              </span>
            }
          >
            <div className={T_WRAP}>
              <table className="w-full">
                <thead className={T_HEAD}>
                  <tr>
                    <th className={T_TH}>Item</th>
                    <th className={T_TH}>Detail</th>
                    <th className={T_TH}>Dated</th>
                    <th className={`${T_TH} text-right`}>Amount</th>
                    {isDraft && <th className={T_TH} />}
                  </tr>
                </thead>
                <tbody>
                  {g.items.map((it) => {
                    const l = itemLabel(it);
                    const rk = `${it.kind}-${it.id}`;
                    return (
                      <tr key={rk} className={T_ROW}>
                        <td className={T_TD}>{l.what}</td>
                        <td className={T_TD}>{l.detail || "—"}</td>
                        <td className={T_TD}>{fmtDay(it.dated)}</td>
                        <td className={`${T_NUM} ${Number(it.amount) < 0 ? "text-risk" : ""}`}>{money(it.amount)}</td>
                        {isDraft && (
                          <td className={`${T_TD} text-right`}>
                            {confirmRemove === rk ? (
                              <span className="inline-flex flex-wrap items-center justify-end gap-2">
                                <span className="text-[14px] text-ink">Leave out of this batch? It'll go in the next one.</span>
                                <Button
                                  size="sm"
                                  disabled={act.isPending}
                                  onClick={() =>
                                    act.mutate({
                                      path: "remove",
                                      body: { kind: it.kind, id: it.id },
                                      ok: "Left out of this batch — it carries forward to the next one.",
                                    })
                                  }
                                >
                                  Leave out
                                </Button>
                                <Button size="sm" variant="ghost" onClick={() => setConfirmRemove(null)}>
                                  Cancel
                                </Button>
                              </span>
                            ) : (
                              <Button variant="ghost" size="sm" disabled={act.isPending} onClick={() => setConfirmRemove(rk)}>
                                Remove
                              </Button>
                            )}
                          </td>
                        )}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </Section>
        ))
      )}

      {leftOut.length > 0 && (
        <Section title="Left out of this batch — carries forward to the next one">
          <div className={T_WRAP}>
            <table className="w-full">
              <thead className={T_HEAD}>
                <tr>
                  <th className={T_TH}>Rep</th>
                  <th className={T_TH}>Item</th>
                  <th className={T_TH}>Detail</th>
                  <th className={`${T_TH} text-right`}>Amount</th>
                  {isDraft && <th className={T_TH} />}
                </tr>
              </thead>
              <tbody>
                {leftOut.map((it) => {
                  const l = itemLabel(it);
                  return (
                    <tr key={`${it.kind}-${it.id}`} className={T_ROW}>
                      <td className={T_TD}>{nameOr(it.rep_name)}</td>
                      <td className={T_TD}>{l.what}</td>
                      <td className={T_TD}>{l.detail || "—"}</td>
                      <td className={`${T_NUM} ${Number(it.amount) < 0 ? "text-risk" : ""}`}>{money(it.amount)}</td>
                      {isDraft && (
                        <td className={`${T_TD} text-right`}>
                          <Button
                            variant="outline"
                            size="sm"
                            disabled={act.isPending}
                            onClick={() =>
                              act.mutate({ path: "add-back", body: { kind: it.kind, id: it.id }, ok: "Added back to this batch." })
                            }
                          >
                            Add back
                          </Button>
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Section>
      )}

      {waiting.length > 0 && (
        <Section title="Waiting, not payable yet" intro="Not in any batch until the reason below is cleared.">
          <div className={T_WRAP}>
            <table className="w-full">
              <thead className={T_HEAD}>
                <tr>
                  <th className={T_TH}>Rep</th>
                  <th className={T_TH}>Item</th>
                  <th className={T_TH}>Why it's waiting</th>
                  <th className={`${T_TH} text-right`}>Amount</th>
                </tr>
              </thead>
              <tbody>
                {waiting.map((w) => (
                  <tr key={`${w.kind}-${w.id}`} className={T_ROW}>
                    <td className={T_TD}>{nameOr(w.rep_name)}</td>
                    <td className={T_TD}>{w.kind === "commission" ? `Commission · order #${w.ref ?? "—"}` : "Adjustment"}</td>
                    <td className={`${T_TD} text-warn`}>{w.reasonText}</td>
                    <td className={`${T_NUM} ${Number(w.amount) < 0 ? "text-risk" : ""}`}>{money(w.amount)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Section>
      )}
    </div>
  );
}
